import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { usePortalAuth } from '../context/PortalAuthContext';
import { ApiError, authApi } from '../utils/api';

const profileSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email address'),
});

const passwordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

type ProfileFormValues = z.infer<typeof profileSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

function AccountPage() {
  const { user, refreshUser } = usePortalAuth();
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    formState: { errors: profileErrors, isSubmitting: profileSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: passwordSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  useEffect(() => {
    resetProfile({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
    });
  }, [resetProfile, user?.email, user?.first_name, user?.last_name]);

  const onProfileSubmit = async (values: ProfileFormValues) => {
    setProfileError(null);
    setProfileSuccess(null);

    try {
      await authApi.updateProfile(values);
      await refreshUser();
      setProfileSuccess('Account information updated successfully.');
    } catch (error) {
      if (error instanceof ApiError) {
        setProfileError(error.message);
        return;
      }
      setProfileError('Unable to update account information');
    }
  };

  const onPasswordSubmit = async (values: PasswordFormValues) => {
    setPasswordError(null);
    setPasswordSuccess(null);

    try {
      await authApi.updatePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      setPasswordSuccess('Password updated successfully.');
      resetPassword({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setPasswordError(error.message);
        return;
      }
      setPasswordError('Unable to update password');
    }
  };

  if (!user) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">You must be signed in to view account settings.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 700 }}>
        <CardContent>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Account Settings
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Update your profile information and password.
          </Typography>

          <Stack spacing={3}>
            <Box component="form" onSubmit={handleSubmitProfile(onProfileSubmit)}>
              <Stack spacing={2}>
                <Typography variant="h6">Profile Information</Typography>
                {profileError && <Alert severity="error">{profileError}</Alert>}
                {profileSuccess && <Alert severity="success">{profileSuccess}</Alert>}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="First Name"
                    error={Boolean(profileErrors.first_name)}
                    helperText={profileErrors.first_name?.message}
                    {...registerProfile('first_name')}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Last Name"
                    error={Boolean(profileErrors.last_name)}
                    helperText={profileErrors.last_name?.message}
                    {...registerProfile('last_name')}
                    required
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Email"
                  type="email"
                  error={Boolean(profileErrors.email)}
                  helperText={profileErrors.email?.message}
                  {...registerProfile('email')}
                  required
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={profileSubmitting}>
                  {profileSubmitting ? 'Saving...' : 'Save Profile'}
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Box component="form" onSubmit={handleSubmitPassword(onPasswordSubmit)}>
              <Stack spacing={2}>
                <Typography variant="h6">Change Password</Typography>
                {passwordError && <Alert severity="error">{passwordError}</Alert>}
                {passwordSuccess && <Alert severity="success">{passwordSuccess}</Alert>}
                <TextField
                  label="Current Password"
                  type="password"
                  error={Boolean(passwordErrors.current_password)}
                  helperText={passwordErrors.current_password?.message}
                  {...registerPassword('current_password')}
                  required
                  fullWidth
                />
                <TextField
                  label="New Password"
                  type="password"
                  error={Boolean(passwordErrors.new_password)}
                  helperText={passwordErrors.new_password?.message}
                  {...registerPassword('new_password')}
                  required
                  fullWidth
                />
                <TextField
                  label="Confirm New Password"
                  type="password"
                  error={Boolean(passwordErrors.confirm_password)}
                  helperText={passwordErrors.confirm_password?.message}
                  {...registerPassword('confirm_password')}
                  required
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={passwordSubmitting}>
                  {passwordSubmitting ? 'Updating...' : 'Update Password'}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default AccountPage;
